import { useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase';

/**
 * PayPal's own payment buttons.
 *
 * The vendor approves inside PayPal's sheet; the card details never touch this
 * page, which is the point — the club never handles them and never has to.
 *
 * Both callbacks talk to our functions rather than to PayPal directly, so the
 * amount is set by the club's pricing and the capture is made with the club's
 * secret. Nothing here is trusted with either.
 */

declare global {
  interface Window {
    paypal?: {
      Buttons: (config: unknown) => {
        render: (el: HTMLElement) => Promise<void>;
        close?: () => void;
      };
    };
  }
}

const fns = getFunctions(app, 'us-east1');
const createOrder = httpsCallable(fns, 'createPaypalOrderFn');
const captureOrder = httpsCallable(fns, 'capturePaypalOrderFn');

/** Load PayPal's SDK once, no matter how many times this mounts. */
let sdkPromise: Promise<void> | null = null;
function loadSdk(clientId: string): Promise<void> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    if (window.paypal) return resolve();
    const s = document.createElement('script');
    s.src =
      `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}` +
      '&currency=USD&intent=capture&disable-funding=credit,paylater';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Could not load PayPal.'));
    document.head.appendChild(s);
  });
  return sdkPromise;
}

export interface PayPalPanelProps {
  reservationId: string;
  amountLabel: string;
  onPaid: () => void;
  onFailed: (message: string) => void;
}

export function PayPalPanel({
  reservationId,
  amountLabel,
  onPaid,
  onFailed,
}: PayPalPanelProps) {
  const host = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'paying' | 'error'>('loading');
  const [message, setMessage] = useState<string | null>(null);

  /*
   * Callbacks live in refs, and the effect depends only on the reservation.
   *
   * The parent passes inline arrow functions, so their identity changes on
   * every render. Listing them as dependencies re-ran this effect each time
   * and rendered ANOTHER set of PayPal buttons into the same container —
   * three stacked "Pay with PayPal" panels, each able to start its own order.
   */
  const paidRef = useRef(onPaid);
  const failedRef = useRef(onFailed);
  paidRef.current = onPaid;
  failedRef.current = onFailed;

  useEffect(() => {
    const clientId = import.meta.env.VITE_PAYPAL_CLIENT_ID;
    if (!clientId) {
      setState('error');
      setMessage('Card payments are not switched on yet. Please choose another way to pay.');
      return;
    }

    let cancelled = false;

    loadSdk(clientId)
      .then(() => {
        if (cancelled || !host.current || !window.paypal) return;
        setState('ready');
        // Belt and braces: never append to a container that already has a
        // button set in it.
        host.current.innerHTML = '';

        window.paypal
          .Buttons({
            style: { layout: 'vertical', shape: 'rect', label: 'pay', height: 46 },

            // The amount is never sent from here. The function reads the
            // reservation and prices it from the club's own figures.
            createOrder: async () => {
              const res = await createOrder({ reservationId });
              return (res.data as { orderId: string }).orderId;
            },

            onApprove: async (data: { orderID: string }) => {
              setState('paying');
              try {
                await captureOrder({ orderId: data.orderID });
                // The webhook confirms this independently and authoritatively.
                // This call just lets us move the vendor on without waiting.
                paidRef.current();
              } catch (e) {
                // The capture may well have succeeded and only the reply been
                // lost. Never tell somebody a payment failed when it might not
                // have — the webhook settles it either way.
                setState('error');
                const msg =
                  'We could not confirm the payment from here. If it left your ' +
                  'account, your tables are safe and your receipt is on its way. ' +
                  'Please do not pay twice — email the club and quote your ' +
                  'confirmation number.';
                setMessage(msg);
                failedRef.current(msg);
              }
            },

            onCancel: () => {
              setState('ready');
              setMessage('Payment cancelled. Your tables are still held for now.');
            },

            onError: (err: unknown) => {
              setState('error');
              const msg = 'PayPal reported a problem. Please try again, or choose another way to pay.';
              setMessage(msg);
              failedRef.current(msg);
              // eslint-disable-next-line no-console
              console.error('PayPal error', err);
            },
          })
          .render(host.current)
          .catch(() => {
            if (cancelled) return;
            setState('error');
            setMessage('Could not show the PayPal buttons. Please try another way to pay.');
          });
      })
      .catch(() => {
        if (cancelled) return;
        setState('error');
        setMessage('Could not reach PayPal. Please try again, or choose another way to pay.');
      });

    return () => {
      cancelled = true;
      if (host.current) host.current.innerHTML = '';
    };
  }, [reservationId]);

  return (
    <div className="pp">
      {state === 'loading' && <p className="pp__status">Loading PayPal…</p>}
      {state === 'paying' && (
        <p className="pp__status">
          Taking {amountLabel}… please do not close this page.
        </p>
      )}
      <div ref={host} className="pp__host" hidden={state === 'error'} />
      {message && (
        <p className={`pp__msg${state === 'error' ? ' pp__msg--bad' : ''}`}>{message}</p>
      )}
    </div>
  );
}
